/* Scene hero: one full-frame dark image you drag / swipe / scroll through (loops endlessly) */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mod = (a, n) => ((a % n) + n) % n;

  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';

    const scene = root.querySelector('[data-scene]');
    const track = root.querySelector('[data-track]');
    const items = Array.from(root.querySelectorAll('[data-item]'));
    const panels = Array.from(root.querySelectorAll('[data-panel]'));
    const progress = root.querySelector('[data-progress]');
    const n = items.length;
    if (!n) return;

    const interval = parseInt(root.dataset.interval, 10) || 6000;
    const autoplay = root.dataset.autoplay === 'true' && !reducedMotion && n > 1;

    let step = 300;   // distance between products (px)
    let pos = 0;      // which product is in the middle (fractional while dragging)
    let active = 0;
    let timer = null;
    let paused = false;
    let visible = true;
    const lastX = new Array(n).fill(null);

    function measure() {
      const w = scene.clientWidth;
      const h = scene.clientHeight;
      // desktop: all products fit across the frame; phones: the middle one is big and neighbours peek in
      const item = w >= 990 ? Math.min(w * 0.24, h * 0.58, 300) : Math.min(w * 0.7, h * 0.46, 280);
      scene.style.setProperty('--item', `${Math.round(item)}px`);
      step = item * 0.82; // photos overlap slightly so their soft edges meet
    }

    // Signed distance (in products) from the middle, wrapped so the row loops
    const distance = (i) => {
      let d = mod(i - pos, n);
      if (d >= n / 2) d -= n;
      return d;
    };

    function render() {
      const edge = n / 2 - 0.3;
      items.forEach((el, i) => {
        const d = distance(i);
        const ad = Math.abs(d);
        const x = d * step;
        // a product wrapping from one end to the other jumps without animating across the scene
        if (lastX[i] !== null && Math.abs(x - lastX[i]) > step * (n / 2)) {
          el.style.transition = 'none';
          requestAnimationFrame(() => requestAnimationFrame(() => { el.style.transition = ''; }));
        }
        lastX[i] = x;
        el.style.setProperty('--x', `${x.toFixed(1)}px`);
        el.style.setProperty('--o', ad > edge ? 0 : 1);
        el.style.setProperty('--z', 50 - Math.round(ad * 10));
      });
      if (progress) progress.style.width = `${((mod(Math.round(pos), n) + 1) / n) * 100}%`;
    }

    function setActive(i) {
      if (i === active && items[i].classList.contains('is-active')) return;
      active = i;
      items.forEach((el, k) => el.classList.toggle('is-active', k === i));
      panels.forEach((p, k) => {
        const on = k === i;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
        p.querySelectorAll('a, button').forEach((el) => { el.tabIndex = on ? 0 : -1; });
      });
    }

    let animTimer = 0;
    function goTo(i, animate = true) {
      // take the shortest way round the loop
      const from = Math.round(pos);
      let delta = mod(i - from, n);
      if (delta > n / 2) delta -= n;
      if (animate) {
        track.classList.add('is-animating');
        clearTimeout(animTimer);
        animTimer = setTimeout(() => track.classList.remove('is-animating'), 800);
      }
      pos = from + delta;
      setActive(mod(pos, n));
      render();
      schedule();
    }
    const next = () => goTo(active + 1);
    const prev = () => goTo(active - 1);

    function schedule() {
      clearTimeout(timer);
      if (autoplay && !paused && visible && !document.hidden) timer = setTimeout(next, interval);
    }

    // Drag / swipe through the scene
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startY = 0;
    let startPos = 0;
    let pointerId = null;
    let dragEndedAt = 0;

    scene.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('a, .scene__btn, .scene__arrow')) return;
      dragging = true; moved = false; pointerId = e.pointerId;
      startX = e.clientX; startY = e.clientY; startPos = pos;
      track.classList.remove('is-animating');
      clearTimeout(timer);
    });
    scene.addEventListener('pointermove', (e) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!moved) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { dragging = false; return; } // vertical page scroll wins
        moved = true;
        scene.classList.add('is-dragging');
        scene.setPointerCapture(pointerId);
      }
      pos = startPos - dx / step;
      render();
      setActive(mod(Math.round(pos), n));
    });
    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;
      scene.classList.remove('is-dragging');
      if (!moved) return;
      moved = false;
      dragEndedAt = Date.now();
      const dx = e.clientX - startX;
      let target = Math.round(pos);
      // a quick flick moves at least one product
      if (Math.abs(dx) > 40 && target === Math.round(startPos)) target += dx < 0 ? 1 : -1;
      goTo(mod(target, n));
    };
    scene.addEventListener('pointerup', endDrag);
    scene.addEventListener('pointercancel', endDrag);

    // Tap a product: neighbour -> bring it to the middle; middle -> open it
    items.forEach((el, i) => {
      el.addEventListener('click', (e) => {
        if (Date.now() - dragEndedAt < 80) { e.preventDefault(); return; } // the click a drag ends with
        if (i === active) {
          const url = el.dataset.url;
          if (url && url !== '#') window.location.href = url;
        } else {
          goTo(i);
        }
      });
    });

    // Trackpad sideways scrolling moves through the scene
    let wheelTimer = 0;
    scene.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      track.classList.remove('is-animating');
      pos += e.deltaX / step;
      render();
      setActive(mod(Math.round(pos), n));
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => goTo(mod(Math.round(pos), n)), 140);
    }, { passive: false });

    root.querySelector('[data-prev]')?.addEventListener('click', prev);
    root.querySelector('[data-next]')?.addEventListener('click', next);
    scene.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    });

    // Pause while the visitor is interacting, off-screen, or in another tab
    scene.addEventListener('mouseenter', () => { paused = true; clearTimeout(timer); });
    scene.addEventListener('mouseleave', () => { paused = false; schedule(); });
    scene.addEventListener('focusin', () => { paused = true; clearTimeout(timer); });
    scene.addEventListener('focusout', () => { paused = false; schedule(); });
    document.addEventListener('visibilitychange', schedule);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule(); }, { threshold: 0.3 }).observe(scene);
    }

    let resizeRaf = 0;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => { measure(); render(); });
    });

    measure();
    setActive(0);
    render();
    schedule();
  }

  const boot = () => document.querySelectorAll('[data-scene-hero]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
